/**
 * 🌐 SERVICIO DE API - PLAZA RURAL
 * ==================================
 * 
 * EXPLICACIÓN PARA PRINCIPIANTES:
 * Este archivo es como un "mensajero" que va al servidor (backend)
 * a buscar información y la trae de vuelta al sitio web (frontend).
 * 
 * En lugar de tener datos falsos (mock), ahora pedimos datos reales
 * al servidor usando estas funciones.
 * 
 * ¿Cómo funciona?
 * 1. El componente llama a una función (ej: api.getGrains())
 * 2. Esta función hace una petición al servidor
 * 3. El servidor responde con los datos
 * 4. La función devuelve los datos al componente
 */

// Usa la base URL del navegador, si es localhost usa el puerto 8001
const host = window.location.hostname;
const protocol = window.location.protocol;
let API_HOST = `${protocol}//${host}`;
if (host.includes('localhost')) {
  API_HOST = `${API_HOST}:8001`;
}

const API_BASE_URL = `${API_HOST}`;

/**
 * Función auxiliar para manejar errores de la API
 * Si algo sale mal, muestra un mensaje claro
 */
const handleResponse = async (response) => {
  // Si la respuesta no es exitosa (código 200-299)
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Error ${response.status}: ${error}`);
  }
  // Si todo está bien, convierte la respuesta a JSON
  return response.json();
};

/**
 * OBJETO API
 * ===========
 * Contiene todas las funciones para pedir datos al servidor
 */
export const api = {

  /**
   * 1️⃣ OBTENER DATOS DE MERCADOS FINANCIEROS
   * Usado en: MarketTicker.jsx
   */
  getMarketData: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/market-data`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching market data:', error);
      throw error;
    }
  },

  /**
   * 2️⃣ OBTENER DATOS DE GRANOS
   * Usado en: GrainsTicker.jsx
   */
  getGrains: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/grains-data`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching grains data:', error);
      throw error;
    }
  },

  /**
   * 3️⃣ OBTENER PROMEDIOS DE UN REMATE ESPECÍFICO
   * Usado en: LivePricesSection.jsx, MonitorSection.jsx
   * @param {number} numeroRemate - Número del remate (requerido)
   */
  getPromediosRemate: async (numeroRemate) => {
    try {
      if (!numeroRemate) {
        throw new Error('numeroRemate es requerido');
      }
      const response = await fetch(`${API_BASE_URL}/api/remates/${numeroRemate}/promedios`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching promedios remate:', error);
      throw error;
    }
  },

  /**
   * 3️⃣A OBTENER LISTA DE REMATES DISPONIBLES
   * Retorna los últimos 12 remates con estado 2,3,4 (Activo, En Curso, Finalizado)
   */
  getRematesList: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/remates-list`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching remates list:', error);
      throw error;
    }
  },

  /**
   * 3️⃣B OBTENER REMATES PRÓXIMOS (3 MESES)
   * Retorna remates de los próximos 3 meses sin importar el estado
   */
  getRematesProximos: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/remates/proximos`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching próximos remates:', error);
      throw error;
    }
  },

  /**
   * 3️⃣C OBTENER PRÓXIMO REMATE PARA HERO
   * Retorna el próximo remate activo o en curso con fecha
   */
  getProximoRemate: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/remates/proximo`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching próximo remate:', error);
      throw error;
    }
  },

  /**
   * 3️⃣D OBTENER CALENDARIO DE REMATES DE UN AÑO
   * Retorna todos los remates del año con sus fechas
   */
  getRematesCalendario: async (year) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/remates/calendario/${year}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching calendario remates:', error);
      throw error;
    }
  },

  /**
   * 4️⃣ OBTENER NOTICIAS
   * Usado en: ModernNews.jsx, NewsWithSidebar.jsx, NoticiasPage.jsx
   * @param {number} limit - Cantidad de noticias a obtener (default: 12)
   */
  getNews: async (limit = 12) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/news?limit=${limit}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching news:', error);
      throw error;
    }
  },

  /**
   * 4️⃣B OBTENER DETALLE DE NOTICIA
   * Usado en: NoticiaDetallePage.jsx
   * @param {string} slug - Slug o ID de la noticia
   */
  getNoticiaDetalle: async (slug) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/noticias/${slug}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching noticia detalle:', error);
      throw error;
    }
  },

  /**
   * 5️⃣ OBTENER ESTADÍSTICAS
   * Usado en: ModernStats.jsx
   */
  getStats: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stats`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  },

  /**
   * 6️⃣ OBTENER SPONSORS/LOGOS
   * Usado en: SponsorsBar.jsx
   */
  getSponsors: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sponsors`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      throw error;
    }
  },

  /**
   * 6️⃣B OBTENER LOGOS DE ESCRITORIOS
   * Usado en: SponsorsBar.jsx
   */
  getEscritorios: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/escritorios`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching escritorios:', error);
      throw error;
    }
  },

  /**
   * 7️⃣ OBTENER ACCESOS RÁPIDOS
   * Usado en: QuickAccessSection.jsx
   */
  getQuickAccess: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quick-access`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching quick access:', error);
      throw error;
    }
  },

  /**
   * 7️⃣B OBTENER DETALLE DE LOTES DE UN REMATE
   * Usado en: LotDetailsSection.jsx
   */
  getLotesRemate: async (remateId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/remates/${remateId}/lotes`);
      return handleResponse(response);
    } catch (error) {
      console.error(`Error fetching lotes for remate ${remateId}:`, error);
      throw error;
    }
  },

  /**
   * 7️⃣C OBTENER INFORMACIÓN DETALLADA DE UN REMATE
   * Usado en: RemateDetailsPage.jsx (example)
   * @param {number} remateId - ID del remate
   */
  getRemateInfo: async (remateId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/remates/${remateId}/info`);
      return handleResponse(response);
    } catch (error) {
      console.error(`Error fetching remate info for remate ${remateId}:`, error);
      throw error;
    }
  },

  /**
   * 8️⃣ OBTENER DATOS DEL CLIMA
   * Usado en: NewsWithSidebar.jsx
   */
  getWeather: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/weather`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching weather:', error);
      throw error;
    }
  },

  /**
   * 8️⃣B OBTENER COTIZACIONES DE CLAIM
   * Usado en: NewsWithSidebar.jsx
   */
  getCotizacionesClaim: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cotizaciones`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching cotizaciones from Claim:', error);
      throw error;
    }
  },

  /**
   * 9️⃣ OBTENER COTIZACIONES HISTÓRICAS
   * Usado en: NewsWithSidebar.jsx
   */
  getCotizaciones: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cotizaciones-historicas`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching cotizaciones:', error);
      throw error;
    }
  },

  /**
   * 🔟 OBTENER FILTROS DISPONIBLES PARA LOTES
   * Usado en: LotesPage.jsx
   * Retorna todas las opciones únicas para cada tipo de filtro
   */
  getLotesFiltros: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/lotes/filtros`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching lotes filtros:', error);
      throw error;
    }
  },

  /**
   * 1️⃣1️⃣ OBTENER DETALLE DE UN LOTE ESPECÍFICO
   * Usado en: LoteDetallePage.jsx
   * Retorna toda la información de un lote específico
   * 
   * @param {number} numeroLote - Número del lote a buscar
   */
  getLoteDetalle: async (numeroLote) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/lotes/${numeroLote}`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching lote detalle:', error);
      throw error;
    }
  },

  /**
   * 1️⃣2️⃣ OBTENER LOTES CON FILTROS
   * Usado en: LotesPage.jsx
   * Retorna lotes filtrados según los parámetros proporcionados
   * 
   * @param {Object} filtros - Objeto con los filtros a aplicar
   * Ejemplo: { tipo_ganado: 'Vacunos', categoria: 'Terneros', peso_min: 150 }
   */
  getLotes: async (filtros = {}) => {
    try {
      // Construir query params
      const params = new URLSearchParams();

      // Agregar cada filtro a los params
      Object.entries(filtros).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          // Si es un array, agregar TODOS los valores (filtros múltiples)
          if (Array.isArray(value) && value.length > 0) {
            value.forEach(v => params.append(key, v));
          } else if (!Array.isArray(value)) {
            params.append(key, value);
          }
        }
      });

      const queryString = params.toString();
      const url = queryString
        ? `${API_BASE_URL}/api/lotes?${queryString}`
        : `${API_BASE_URL}/api/lotes`;

      console.log('[API] getLotes URL:', url);
      console.log('[API] getLotes filtros:', filtros);

      const response = await fetch(url);
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching lotes:', error);
      throw error;
    }
  },

  /**
   * 1️⃣3️⃣ LOGIN DE USUARIOS
   * Usado en: LoginPage.jsx
   */
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  /**
   * 1️⃣4️⃣ PREOFERTAR (MOCK)
   * Usado en: PreofertaModal.jsx
   */
  preofertar: async (data) => {
    // Simular delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('[API] Preoferta recibida:', data);
    return { success: true, message: 'Preoferta registrada' };
  }
};

// Exportar por defecto
export default api;
